import React, { useEffect, forwardRef, useState } from "react";
import { Form, Dropdown } from "react-bootstrap";
import { parse, isValid, add, addDays, subDays, isAfter, differenceInDays } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import * as Yup from "yup";
import axios from "axios";
import QRCode from "qrcode";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe, faShare, faCopy } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faWhatsapp,
  faInstagram,
  faLinkedin,
  faYoutube,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import generateExcel from "zipcelx";
import { domainConfig } from "../config.js";

export const getSignatureFileUrl = (dataTable, fileName) => {
  const eventId = dataTable?.event_id || dataTable?.evento_id;
  if (!fileName || !eventId) return "";

  const imageServer = dataTable?.json?.server?.imageServer || domainConfig.imageServer;
  if (!imageServer) return "";

  return `${String(imageServer).replace(/\/$/, "")}/plataforma-eventos/${encodeURIComponent(eventId)}/signatures/${encodeURIComponent(fileName)}`;
};

export const downloadSignatureFile = async (dataTable, fileName) => {
  const url = getSignatureFileUrl(dataTable, fileName);
  if (!url) return false;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Signature download failed");
    const objectUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return true;
  } catch (error) {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return false;
  }
};

const ORIGIN_LABELS = {
  p1_createParticipants_001: "Painel: cadastro manual",
  p2_createParticipants_001: "Painel: cadastro manual",
  p1_eventEdition_001: "Painel: edição de evento",
  p2_eventEdition_001: "Painel: edição de evento",
  p1_externalApi_001: "API externa",
  p2_externalApi_001: "API externa",
  p1_webappRegister_001: "Webapp: formulário de inscrição",
  p2_webappRegister_001: "Webapp: formulário de inscrição",
  p2_checkinCreateParticipants_001: "Credenciamento: check-in",
  p2_publicRegisterCreateParticipants_001: "Credenciamento: formulário de inscrição",
  p2_symplaCron_001: "Integração Sympla",
};

export function getOriginLabel(origin) {
  if (!origin) return null;
  return ORIGIN_LABELS[origin] || `Origem desconhecida (${origin})`;
}

export function validateEmailDomain(email, updatedValues) {
  const normalizedEmail = typeof email === 'string' ? email.trim() : email;
  // If value is empty/invalid, do not domain-validate here.
  if (typeof normalizedEmail !== 'string' || normalizedEmail === '' || !normalizedEmail.includes('@')) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      return { valid: false, error: "formato" };
    }
  }

  // Check domain restriction
  const validEmailsConfig = updatedValues?.json?.form?.validEmails;
  const domainRestrictionEnabled = validEmailsConfig?.status === true
    || validEmailsConfig?.status === 1
    || validEmailsConfig?.status === "1"
    || validEmailsConfig?.status === "true";

  if (domainRestrictionEnabled) {
    const normalizedDomains = (Array.isArray(validEmailsConfig.list) ? validEmailsConfig.list : [])
      .filter((domain) => typeof domain === 'string' && domain.trim() !== '')
      .map(domain => {
        const trimmed = domain.trim();
        const withAt = trimmed.startsWith('@') ? trimmed : '@' + trimmed;
        return withAt.toLowerCase();
      });

    if (normalizedDomains.length === 0) {
      return {
        valid: false,
        error: "domain-config",
        allowedDomains: "(nenhum dominio configurado)",
      };
    }

    const atIndex = normalizedEmail.lastIndexOf("@");
    if (atIndex < 0) {
      return { valid: false, error: "formato" };
    }

    const emailDomain = normalizedEmail.substring(atIndex).toLowerCase();

    if (normalizedDomains.length > 0) {
      const isDomainAllowed = normalizedDomains.some(
        allowedDomain => emailDomain === allowedDomain
      );

      if (!isDomainAllowed) {
        return {
          valid: false,
          error: "domínio",
          allowedDomains: normalizedDomains.join(", "),
        };
      }
    }
  }

  return { valid: true };
}

export function redirectURL(open, url) {
  if (open === true) {
    window.open(url, "_blank");
  } else {
    window.location.replace(url);
  }
}

export const flattenErrors = (obj, parentKey = "", result = {}) => {
  for (let key in obj) {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;
    if (typeof obj[key] === "object" && obj[key] !== null) {
      flattenErrors(obj[key], fullKey, result);
    } else {
      result[fullKey] = obj[key];
    }
  }
  return result;
};

export const isBackground = (str) => {
  return CSS.supports("background", str);
};

export const isColor = (strColor) => {
  const s = new Option().style;
  s.color = strColor;
  return s.color !== "";
};

const parseColorChannel = (value, max = 255) => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return null;

  const channel = String(value).trim().endsWith("%")
    ? (parsed / 100) * max
    : parsed;

  return Math.min(max, Math.max(0, channel));
};

const parseComputedColor = (color) => {
  if (!color || typeof document === "undefined" || !document.body) return null;

  const probe = document.createElement("span");
  probe.style.color = String(color).trim();
  if (!probe.style.color) return null;

  document.body.appendChild(probe);
  const computedColor = window.getComputedStyle(probe).color;
  probe.remove();

  const match = computedColor.match(/^rgba?\((.*)\)$/i);
  if (!match) return null;

  const channels = match[1]
    .replace(/\//g, " ")
    .split(/[\s,]+/)
    .filter(Boolean);
  if (channels.length < 3) return null;

  const red = parseColorChannel(channels[0]);
  const green = parseColorChannel(channels[1]);
  const blue = parseColorChannel(channels[2]);
  const alpha = channels[3] === undefined
    ? 1
    : parseColorChannel(channels[3], 1);

  if ([red, green, blue, alpha].some((value) => value === null)) return null;

  return { red, green, blue, alpha };
};

const compositeColor = (foreground, background) => ({
  red: foreground.red * foreground.alpha + background.red * (1 - foreground.alpha),
  green: foreground.green * foreground.alpha + background.green * (1 - foreground.alpha),
  blue: foreground.blue * foreground.alpha + background.blue * (1 - foreground.alpha),
  alpha: 1,
});

const relativeLuminance = ({ red, green, blue }) => {
  const toLinear = (channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };

  return (
    0.2126 * toLinear(red) +
    0.7152 * toLinear(green) +
    0.0722 * toLinear(blue)
  );
};

export const getContrastColor = (color, backgroundColor = "#ffffff") => {
  if (!color) return "#ffffff";

  const foreground = parseComputedColor(color);
  if (!foreground) return null;

  const parsedBackground = parseComputedColor(backgroundColor);
  if (!parsedBackground) return null;
  const opaqueBackground = compositeColor(
    parsedBackground,
    { red: 255, green: 255, blue: 255, alpha: 1 },
  );
  const effectiveColor = compositeColor(foreground, opaqueBackground);
  const colorLuminance = relativeLuminance(effectiveColor);
  const blackContrast = (colorLuminance + 0.05) / 0.05;
  const whiteContrast = 1.05 / (colorLuminance + 0.05);

  return whiteContrast > blackContrast ? "#ffffff" : "#000000";
};

export function convertMilliseconds(milliseconds) {
  const minutes = Math.floor(milliseconds / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  const remainingMilliseconds = milliseconds % 1000;

  return {
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
    milliseconds: String(remainingMilliseconds).padStart(3, "0"),
  };
}

export const HelpTooltip = forwardRef((props, ref) => (
  <span ref={ref} className="ms-1 cursor-pointer">
    <FontAwesomeIcon icon={props.icon} {...props} />
  </span>
));

// Date handlers
export function validateDate(date) {
  // Attempt to parse with seconds
  const parsedDateX = parse(date, "dd/MM/yyyy", new Date());
  const isValidX = isValid(parsedDateX);

  // Attempt to parse with seconds
  const parsedDateWithSeconds = parse(date, "dd/MM/yyyy HH:mm:ss", new Date());
  const isValidWithSeconds = isValid(parsedDateWithSeconds);

  // Attempt to parse without seconds
  const parsedDateWithoutSeconds = parse(date, "dd/MM/yyyy HH:mm", new Date());
  const isValidWithoutSeconds = isValid(parsedDateWithoutSeconds);

  // Return true if either parse is valid
  if (isValidX || isValidWithSeconds || isValidWithoutSeconds) {
    return true;
  }
  return false;
}

export function isDateAfterToday(date) {
  const parsedDateWithSeconds = parse(date, "dd/MM/yyyy HH:mm:ss", new Date());
  const parsedDateWithoutSeconds = parse(date, "dd/MM/yyyy HH:mm", new Date());

  const validParsedDate = isValid(parsedDateWithSeconds)
    ? parsedDateWithSeconds
    : parsedDateWithoutSeconds;

  if (isValid(validParsedDate)) {
    return isAfter(validParsedDate, new Date());
  }
  return false;
}

export const calculateEventDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return "";

  try {
    const start = parse(startDate, "dd/MM/yyyy HH:mm", new Date());
    const end = parse(endDate, "dd/MM/yyyy HH:mm", new Date());

    if (isValid(start) && isValid(end)) {
      const days = differenceInDays(end, start);
      return ` (${startDate} até ${endDate}${days > 0 ? ` - ${days} ${days === 1 ? "dia" : "dias"}` : ""})`;
    }
  } catch (error) {
    console.error("Error calculating duration:", error);
  }
  return "";
};

export const calculateRegisterDuration = (
  registerStartDate,
  registerEndDate,
) => {
  if (!registerStartDate || !registerEndDate) return "";

  try {
    const start = parse(registerStartDate, "dd/MM/yyyy HH:mm", new Date());
    const end = parse(registerEndDate, "dd/MM/yyyy HH:mm", new Date());

    if (isValid(start) && isValid(end)) {
      const days = differenceInDays(end, start);
      return ` (${registerStartDate} até ${registerEndDate}${days > 0 ? ` - ${days} ${days === 1 ? "dia" : "dias"}` : ""})`;
    }
  } catch (error) {
    console.error("Error calculating register duration:", error);
  }
  return "";
};

export function getAddCode(inputString) {
  const regex = /[?&]addCode=([^&#]*)/;
  const matches = inputString.match(regex);
  if (matches) {
    const addCode = matches[1];
    return decodeURIComponent(addCode);
  }
  return null;
}

export function getRandomInterval(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export function isValidMarginFormat(value) {
  const regex =
    /^(\d{1,2}\.\d{2}) (\d{1,2}\.\d{2}) (\d{1,2}\.\d{2}) (\d{1,2}\.\d{2})$/;
  return regex.test(value);
}

export function convertToRem(value) {
  if (value === null || value === undefined) {
    return "";
  }

  const normalizedValue = String(value).trim();

  if (normalizedValue === "") {
    return "";
  }

  const convertTokenToRem = (token) => {
    if (/^-?\d+(\.\d+)?rem$/i.test(token)) {
      return token;
    }

    if (/^-?\d+(\.\d+)?px$/i.test(token)) {
      return `${parseFloat(token) / 16}rem`;
    }

    if (/^-?\d+(\.\d+)?$/.test(token)) {
      return `${parseFloat(token)}rem`;
    }

    return token;
  };

  if (isValidMarginFormat(normalizedValue)) {
    return normalizedValue.split(" ").map(convertTokenToRem).join(" ");
  }

  return normalizedValue.split(/\s+/).map(convertTokenToRem).join(" ");
}

export function generateDateCategories() {
  const monthsInPortuguese = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];
  const today = new Date();
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const daysInMonth = endOfMonth.getDate();
  const categories = [];

  // Helper function to format the date
  function formatDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = monthsInPortuguese[date.getMonth()];
    return `${day} ${month}`;
  }

  // Generate categories for each day of the current month
  for (let i = 1; i <= daysInMonth; i++) {
    const newDate = new Date(today.getFullYear(), today.getMonth(), i);
    categories.push(formatDate(newDate));
  }

  return categories;
}

export const getCollectedParticipantsExportHeaders = (fields, { includeHiddenFields = true } = {}) => (
  (Array.isArray(fields) ? fields : [])
    .filter((field) => (
      field
      && field.inputID !== undefined
      && field.inputID !== null
      && (includeHiddenFields || field.showInUserReport !== false)
    ))
    .map((field) => ({
      value: field.label || field.name || String(field.inputID),
      inputID: field.inputID,
    }))
);

export function getExcelFromObject({ filename, headers = [], data = [] }) {
  const config = {
    filename: filename || "export",
    sheet: {
      data: [],
    },
  };

  const dataSet = config.sheet.data;
  const safeHeaders = Array.isArray(headers)
    ? headers.filter((header) => header && header.inputID !== undefined && header.inputID !== null)
    : [];
  const safeData = Array.isArray(data) ? data : [];
  const parseObject = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch (error) {
        return {};
      }
    }
    return {};
  };
  const toExportValue = (value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return value.map(toExportValue).join(", ");
    if (typeof value === "object") {
      if (Object.prototype.hasOwnProperty.call(value, "value")) return toExportValue(value.value);
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  };

  // Add headers to the dataset
  const headerRow = [
    { value: "E-mail", type: "string" }, // Email header
    ...safeHeaders.map((header) => ({
      value: String(header.value),
      type: "string",
    })),
    { value: "Observação", type: "string" }, // Add json_collected.observacao
    { value: "Data", type: "string" }, // Add collected date
  ];
  dataSet.push(headerRow);

  // Map data to match the headers using inputID
  safeData.forEach((item) => {
    const profile = parseObject(item?.json);
    const collected = parseObject(item?.json_collected);
    // Start with the email in the row
    const dataRow = [
      {
        value: item.email || "",
        type: "string",
      },
    ];

    // Map the other fields based on headers
    dataRow.push(
      ...safeHeaders.map((header) => {
        const { inputID } = header;
        const jsonEntry = profile[inputID] ?? profile[String(inputID)];
        return {
          value: toExportValue(jsonEntry),
          type: "string",
        };
      }),
    );
    dataRow.push({
      value: toExportValue(collected.observacao),
      type: "string",
    });

    // Add the date_collected value
    dataRow.push({
      value: item?.date_collected || "",
      type: "string",
    });

    dataSet.push(dataRow);
  });

  // Generate and download the Excel file
  return generateExcel(config);
}

const sanitizeQRCodeFilename = (value, fallback = "qrcode") => {
  const normalizedValue =
    typeof value === "string" || typeof value === "number"
      ? String(value).trim()
      : "";

  const withoutAccents =
    typeof normalizedValue.normalize === "function"
      ? normalizedValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      : normalizedValue;

  const sanitizedValue = withoutAccents
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 31 && !['<', '>', ':', '"', '/', '\\', '|', '?', '*'].includes(char);
    })
    .join("")
    .replace(/\s+/g, " ")
    .replace(/\.+$/g, "")
    .trim();

  return sanitizedValue || fallback;
};

const getQRCodeDownloadBaseName = (item, platform) => {
  if (platform === 1) {
    return sanitizeQRCodeFilename(item?.visitante_id, "participante");
  }

  if (Number(item?.type) === 2) {
    return sanitizeQRCodeFilename(
      item?.exhibitor_name || item?.name || item?.json?.name || item?.userName,
      item?.exhibitor_id ? `expositor_${item.exhibitor_id}` : "expositor",
    );
  }

  return sanitizeQRCodeFilename(item?.email || item?.user_id, "qrcode");
};

const getUniqueQRCodeFilename = (baseName, usedNames) => {
  const normalizedKey = String(baseName).toLowerCase();
  const currentCount = usedNames.get(normalizedKey) || 0;

  usedNames.set(normalizedKey, currentCount + 1);

  return currentCount === 0
    ? `${baseName}.png`
    : `${baseName} (${currentCount + 1}).png`;
};

export const downloadQRCode = async (link, title = "qrcode") => {
  try {
    const qrCodeDataUrl = await QRCode.toDataURL(link, { width: 500 });
    const fileName = sanitizeQRCodeFilename(title);

    // Create a link element
    const aTag = document.createElement("a");
    aTag.href = qrCodeDataUrl;
    aTag.download = `${fileName}.png`;
    document.body.appendChild(aTag);
    aTag.click();
    document.body.removeChild(aTag);
  } catch (error) {
    console.error("Error generating QR Code: ", error);
  }
};

export const createQRCodePlatform01 = (user) => {
  const code = `flex-id-` + user.visitante_id;
  const finalCode = window.btoa(code);
  return finalCode;
};

export const createQRCodePlatform02 = (token, user) => {
  if (user.type === 2) {
    const code = `exhibitor=${user.exhibitor_id}`;
    const codify = window.btoa(code);
    const finalCode = `${domainConfig.eventos}/?token=${encodeURIComponent(token)}&addCode=${encodeURIComponent(codify)}`;
    return finalCode;
  } else {
    const code = `contact=${user.user_id}`;
    const codify = window.btoa(code);
    const finalCode = `${domainConfig.eventos}/?token=${encodeURIComponent(token)}&addCode=${encodeURIComponent(codify)}`;
    return finalCode;
  }
};

export const batchQRCodeDownload = (
  setZipIsReady,
  users,
  event_name,
  event_token,
  notyf,
  platform,
) => {
  const downloadBatchQR = async () => {
    setZipIsReady(false);

    // Show toast notification when starting
    const toastId = notyf.open({
      type: "success",
      message: `Gerando ${users.length} códigos QR...`,
      ripple: true,
      dismissible: false,
      duration: 0, // Don't auto-dismiss
    });

    const zip = new JSZip();
    const usedNames = new Map();

    try {
      // Generate QR codes for all users
      let skipped = 0;
      for (const user of users) {
        // Skip type 2 users with no exhibitor association
        if (platform === 2 && user.type === 2 && !user.exhibitor_id) {
          skipped++;
          continue;
        }
        let code = null;
        if (platform === 1) {
          code = await QRCode.toDataURL(createQRCodePlatform01(user), {
            width: 500,
          });
        } else {
          code = await QRCode.toDataURL(
            createQRCodePlatform02(event_token, user),
            { width: 500 },
          );
        }
        const response = await fetch(code);
        const blob = await response.blob();
        const filename = getUniqueQRCodeFilename(
          getQRCodeDownloadBaseName(user, platform),
          usedNames,
        );
        zip.file(filename, blob);
      }

      // Generate and trigger ZIP download
      zip.generateAsync({ type: "blob" }).then((zipBlob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(zipBlob);
        link.download = event_name + ".zip";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setZipIsReady(true);

        // Dismiss the loading toast and show success toast
        notyf.dismiss(toastId);
        notyf.open({
          type: "success",
          message: `Download de ${users.length - skipped} códigos QR concluído com sucesso!${skipped > 0 ? ` (${skipped} ignorado(s) sem expositor associado)` : ''}`,
          ripple: true,
          dismissible: true,
          duration: 4000,
        });
      });
    } catch (error) {
      console.error("Error generating QR codes:", error);
      setZipIsReady(true);

      // Dismiss the loading toast and show error toast
      notyf.dismiss(toastId);
      notyf.open({
        type: "error",
        message: "Erro ao gerar códigos QR. Tente novamente.",
        ripple: true,
        dismissible: true,
        duration: 4000,
      });
    }
  };

  downloadBatchQR();
};

export function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (e) {
    return value; // If parsing fails, return the original value
  }
}

export function normalizeJsonObject(value, fallback = {}) {
  const parsedValue = safeJsonParse(value);

  return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
    ? parsedValue
    : fallback;
}

export function getJsonFieldValue(field) {
  return field && typeof field === "object" && !Array.isArray(field)
    && Object.prototype.hasOwnProperty.call(field, "value")
    ? field.value
    : field;
}

export function getFirstError(...errors) {
  return errors.find((error) => error !== undefined && error !== null) || "";
}

export const PANEL_EVENT_TIME_ZONE = "America/Sao_Paulo";
const DATE_TIME_PICKER_FORMAT = "dd/MM/yyyy HH:mm";
const DATE_TIME_PICKER_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/;

const isValidDateValue = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const padDatePart = (value) => String(value).padStart(2, "0");

const parseBrazilianDateTimeValue = (value) => {
  const match = value.trim().match(DATE_TIME_PICKER_PATTERN);
  if (!match) return null;

  const [, day, month, year, hour = "0", minute = "0"] = match;
  const wallClockValue = `${year}-${padDatePart(month)}-${padDatePart(day)} ${padDatePart(hour)}:${padDatePart(minute)}:00`;
  const parsedDate = zonedTimeToUtc(wallClockValue, PANEL_EVENT_TIME_ZONE);

  if (!isValidDateValue(parsedDate)) return null;

  const normalizedInput = `${padDatePart(day)}/${padDatePart(month)}/${year} ${padDatePart(hour)}:${padDatePart(minute)}`;
  const normalizedOutput = formatInTimeZone(parsedDate, PANEL_EVENT_TIME_ZONE, DATE_TIME_PICKER_FORMAT);
  return normalizedInput === normalizedOutput ? parsedDate : null;
};

export const parseDateTimePickerValue = (value) => {
  if (!value) return null;
  if (isValidDateValue(value)) return value;

  if (typeof value === "object" && typeof value.format === "function") {
    const momentIsValid = typeof value.isValid === "function"
      ? value.isValid()
      : value._isValid !== false;

    if (!momentIsValid) return null;

    const parsedDate = zonedTimeToUtc(value.format("YYYY-MM-DD HH:mm:ss"), PANEL_EVENT_TIME_ZONE);
    return isValidDateValue(parsedDate) ? parsedDate : null;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim();
    const brazilianDate = parseBrazilianDateTimeValue(normalizedValue);
    if (brazilianDate) return brazilianDate;

    const sqlDate = normalizedValue.match(
      /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/,
    );
    if (sqlDate) {
      const isoLikeValue = normalizedValue.replace(" ", "T");
      const normalizedIsoValue = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(isoLikeValue)
        ? `${isoLikeValue}:00`
        : isoLikeValue.length === 10
          ? `${isoLikeValue}T00:00:00`
          : isoLikeValue;
      const parsedDate = new Date(`${normalizedIsoValue}Z`);
      return isValidDateValue(parsedDate) ? parsedDate : null;
    }

    const parsedNativeDate = new Date(normalizedValue);
    return isValidDateValue(parsedNativeDate) ? parsedNativeDate : null;
  }

  return null;
};

export const normalizeDateTimePickerValue = (value) => parseDateTimePickerValue(value) || value;

export const formatDateTimePickerValue = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const parsedDate = parseDateTimePickerValue(value);
  if (parsedDate) return formatInTimeZone(parsedDate, PANEL_EVENT_TIME_ZONE, DATE_TIME_PICKER_FORMAT);

  return typeof value === "string" ? value : "";
};

const hasDateRangeValue = (value) => value !== null && value !== undefined && value !== "";

export const normalizeDateRange = (dateRange = {}) => {
  const safeDateRange = dateRange || {};
  const hasStartDate = hasDateRangeValue(safeDateRange.startDate);
  const hasEndDate = hasDateRangeValue(safeDateRange.endDate);
  const startDate = hasStartDate ? parseDateTimePickerValue(safeDateRange.startDate) : null;
  const endDate = hasEndDate ? parseDateTimePickerValue(safeDateRange.endDate) : null;

  return {
    startDate,
    endDate,
    valid: (!hasStartDate || Boolean(startDate))
      && (!hasEndDate || Boolean(endDate))
      && (!startDate || !endDate || startDate <= endDate),
  };
};

export const getDefaultDateRangeInTimeZone = (daysBefore, daysAfter, timeZone = PANEL_EVENT_TIME_ZONE) => {
  const now = new Date();
  const startDateKey = formatInTimeZone(subDays(now, daysBefore), timeZone, "yyyy-MM-dd");
  const endDateKey = formatInTimeZone(addDays(now, daysAfter), timeZone, "yyyy-MM-dd");

  return {
    startDate: zonedTimeToUtc(`${startDateKey} 00:00:00`, timeZone),
    endDate: zonedTimeToUtc(`${endDateKey} 23:59:59.999`, timeZone),
  };
};

export function convertDate(date, format = "dd/MM/yyyy HH:mm") {
  const normalizedDate = parseFlexibleDate(date);
  return isValid(normalizedDate) ? formatInTimeZone(normalizedDate, PANEL_EVENT_TIME_ZONE, format) : "";
}

export function convertDateToDisplay(date, dateOnly = false) {
  const normalizedDate = parseFlexibleDate(date);
  if (!isValid(normalizedDate)) return "";

  let datetime;
  if (dateOnly === true) {
    datetime = formatInTimeZone(normalizedDate, PANEL_EVENT_TIME_ZONE, "dd/MM/yyyy");
  } else {
    datetime = formatInTimeZone(normalizedDate, PANEL_EVENT_TIME_ZONE, "dd/MM/yyyy HH:mm");
  }
  return datetime;
}

export function parseFlexibleDate(date) {
  if (!date) return new Date(NaN);
  if (date instanceof Date) return new Date(date.getTime());
  if (typeof date === "number") return new Date(date);

  const value = String(date).trim();
  if (!value) return new Date(NaN);

  const brazilianDate = value.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/,
  );
  if (brazilianDate) {
    return parseDateTimePickerValue(value) || new Date(NaN);
  }

  const sqlDate = value.match(
    /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/,
  );
  if (sqlDate) {
    const normalizedValue = value.replace(" ", "T");
    if (value.length === 10) return new Date(`${normalizedValue}T00:00:00Z`);
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalizedValue)) {
      return new Date(`${normalizedValue}:00Z`);
    }
    return new Date(`${normalizedValue}Z`);
  }

  return new Date(value);
}

export const isFutureDate = (date, referenceDate = new Date()) => {
  const parsedDate = parseFlexibleDate(date);
  const reference = referenceDate instanceof Date
    ? referenceDate
    : new Date(referenceDate);

  return isValid(parsedDate) && isValid(reference)
    && parsedDate.getTime() > reference.getTime();
};

export const getEffectiveEventStatus = (status, eventEndDate) =>
  isFutureDate(eventEndDate) ? 2 : status;

export const isRegistrationOpen = (status, registrationEndDate) =>
  status === true
  || status === 1
  || status === "1"
  || status === "true"
  || isFutureDate(registrationEndDate);

export function getDateTimestamp(date) {
  const normalizedDate = parseFlexibleDate(date);
  const timestamp = normalizedDate.getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function convertDateToDisplayA(date, tipo) {
  if (!date) return false;

  const dateToFormat = parseFlexibleDate(date);

  if (!isValid(dateToFormat)) {
    return false;
  }

  let datetime;
  if (tipo === 1) {
    datetime = formatInTimeZone(dateToFormat, "America/Sao_Paulo", "dd/MM");
  } else {
    datetime = formatInTimeZone(
      dateToFormat,
      "America/Sao_Paulo",
      "dd/MM/yyyy HH:mm",
    );
  }
  return datetime;
}

export function textToArray(text) {
  return text.split(/\r?\n/);
}

export function defaultCreationDate() {
  const date = formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm");
  return date;
}

export function defaultEndDate() {
  const date = formatInTimeZone(add(new Date(), { days: "7" }), "UTC", "yyyy-MM-dd HH:mm");
  return date;
}

export function defaultEndDateAgenda() {
  const date = formatInTimeZone(add(new Date(), { hours: "1" }), "UTC", "yyyy-MM-dd HH:mm");
  return date;
}

// Social Media Share URLs
export function handleYoutubeUrl(value) {
  let regex =
    /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm;
  let id = regex.exec(value);
  if (id && id[3]) {
    return id[3];
  } else {
    return;
  }
}

export const createShareWhats = (url, title, description) => {
  const encodedUrl = encodeURIComponent(url);
  const shareText = description ? `${title} - ${description}` : `${title} -`;
  const encodedText = encodeURIComponent(shareText);

  return `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
};

export const handleSocialShare = (platform, url) => {
  switch (platform) {
    case "whatsapp":
      window.open(
        url,
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes",
      );
      break;
    default:
      break;
  }
};

export const ShareButton = ({
  shareUrl,
  title,
  description,
  notyf,
  className,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  return (
    <Dropdown show={showDropdown} onToggle={setShowDropdown}>
      <Dropdown.Toggle
        variant={"primary"}
        className={`btn btn-sm btn-primary ${className}`}
        id="share-dropdown"
      >
        <FontAwesomeIcon icon={faShare} className="me-1" />
        Compartilhar
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item onClick={() => handleShare(shareUrl, notyf)}>
          <FontAwesomeIcon icon={faCopy} className="me-2" />
          Copiar link
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={() =>
            handleSocialShare(
              "whatsapp",
              createShareWhats(shareUrl, title, description),
            )
          }
        >
          <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
          WhatsApp
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export const TextWithLineBreaks = ({ text }) => {
  if (!text || typeof text !== "string") {
    return null;
  }

  const lines = text.split(/\\n|\n/);

  const formattedText = lines.map((line, index) => {
    // Process formatting tags in the line
    const processedLine = processFormattingTags(line);

    return (
      <React.Fragment key={index}>
        {processedLine}
        {index !== lines.length - 1 && <br />}
      </React.Fragment>
    );
  });

  return <span>{formattedText}</span>;
};

const processFormattingTags = (text) => {
  if (!text) return text;

  // Split the text by opening and closing tags
  const parts = [];
  let currentIndex = 0;
  let remainingText = text;

  // Process bold tags
  while (
    remainingText.indexOf("<b>") !== -1 &&
    remainingText.indexOf("</b>") !== -1
  ) {
    const startIdx = remainingText.indexOf("<b>");
    const endIdx = remainingText.indexOf("</b>");

    if (startIdx !== -1 && endIdx !== -1 && startIdx < endIdx) {
      // Add text before the tag
      if (startIdx > 0) {
        parts.push(remainingText.substring(0, startIdx));
      }

      // Add the bold text
      const boldText = remainingText.substring(startIdx + 3, endIdx);
      parts.push(
        <span className="fw-bold" key={`bold-${currentIndex}`}>
          {processFormattingTags(boldText)}
        </span>,
      );

      // Update remaining text and index
      remainingText = remainingText.substring(endIdx + 4);
      currentIndex++;
    } else {
      break;
    }
  }

  // Process italic tags in the remaining text
  while (
    remainingText.indexOf("<i>") !== -1 &&
    remainingText.indexOf("</i>") !== -1
  ) {
    const startIdx = remainingText.indexOf("<i>");
    const endIdx = remainingText.indexOf("</i>");

    if (startIdx !== -1 && endIdx !== -1 && startIdx < endIdx) {
      // Add text before the tag
      if (startIdx > 0) {
        parts.push(remainingText.substring(0, startIdx));
      }

      // Add the italic text
      const italicText = remainingText.substring(startIdx + 3, endIdx);
      parts.push(
        <span className="fst-italic" key={`italic-${currentIndex}`}>
          {processFormattingTags(italicText)}
        </span>,
      );

      // Update remaining text and index
      remainingText = remainingText.substring(endIdx + 4);
      currentIndex++;
    } else {
      break;
    }
  }

  // Add any remaining text
  if (remainingText.length > 0) {
    parts.push(remainingText);
  }

  return parts.length > 0 ? parts : text;
};

export function handleShare(fullDomain, notyf) {
  navigator.clipboard.writeText(fullDomain);
  notyf.open({
    type: "success",
    message: "Copiado para a área de transferência",
    ripple: true,
    dismissible: true,
  });
}

function cloneMissingProperty(value) {
  if (Array.isArray(value)) {
    return value.map((item) => cloneMissingProperty(item));
  }

  if (value && typeof value === "object") {
    return Object.keys(value).reduce((accumulator, key) => {
      accumulator[key] = cloneMissingProperty(value[key]);
      return accumulator;
    }, {});
  }

  return value;
}

export function fillMissingProperties(baseObject, comparisonObject) {
  for (const key in comparisonObject) {
    if (comparisonObject.hasOwnProperty(key)) {
      const isObject =
        typeof comparisonObject[key] === "object" &&
        comparisonObject[key] !== null &&
        !Array.isArray(comparisonObject[key]);
      const isArray = Array.isArray(comparisonObject[key]);

      if (isObject) {
        if (
          !baseObject[key] ||
          typeof baseObject[key] !== "object" ||
          Array.isArray(baseObject[key])
        ) {
          baseObject[key] = {};
        }
        fillMissingProperties(baseObject[key], comparisonObject[key]);
      } else if (isArray) {
        if (!Array.isArray(baseObject[key])) {
          baseObject[key] = cloneMissingProperty(comparisonObject[key]);
          continue;
        }

        if (key === "questions" && comparisonObject[key].length === 1) {
          // Special handling for 'questions' array where comparisonObject always has one object
          const comparisonItem = comparisonObject[key][0];
          for (let i = 0; i < baseObject[key].length; i++) {
            if (typeof baseObject[key][i] === "object") {
              fillMissingProperties(baseObject[key][i], comparisonItem);
            }
          }
        } else {
          for (let i = 0; i < baseObject[key].length; i++) {
            if (typeof baseObject[key][i] === "object") {
              if (!comparisonObject[key][i]) {
                continue;
              }

              fillMissingProperties(
                baseObject[key][i],
                comparisonObject[key][i],
              );
            }
          }
        }
      } else {
        if (baseObject[key] === undefined) {
          baseObject[key] = comparisonObject[key];
        }
      }
    }
  }
  return baseObject;
}

// Table filters
export function NumberRangeColumnFilter({
  column: { filterValue = [], preFilteredRows, setFilter, id },
}) {
  const [min, max] = React.useMemo(() => {
    if (!preFilteredRows.length) {
      return [0, 0]; // No rows, return default min/max
    }

    // Initialize min and max with the first valid value
    let min = Number(preFilteredRows[0].values[id]) || 0;
    let max = min;

    preFilteredRows.forEach((row) => {
      const value = Number(row.values[id]);
      if (!isNaN(value)) {
        min = Math.min(value, min);
        max = Math.max(value, max);
      }
    });

    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <div className="d-flex mt-2">
      <Form.Control
        value={filterValue[0] || ""}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [
            val ? parseInt(val, 10) : undefined,
            old[1],
          ]);
        }}
        placeholder={`Min (${min})`}
        style={{
          width: "110px",
        }}
      />
      <span className="mx-2 mt-1">até</span>
      <Form.Control
        value={filterValue[1] || ""}
        type="number"
        onChange={(e) => {
          const val = e.target.value;
          setFilter((old = []) => [
            old[0],
            val ? parseInt(val, 10) : undefined,
          ]);
        }}
        placeholder={`Max (${max})`}
        style={{
          width: "110px",
        }}
      />
    </div>
  );
}

export function SelectColumnFilter({
  column: {
    filterValue,
    setFilter,
    preFilteredRows,
    id,
    renderFilterLabel,
    emptyOptionLabel,
    allOptionLabel,
  },
  gotoPage,
}) {
  const { options, hasEmpty } = React.useMemo(() => {
    const options = new Set();
    let hasEmpty = false;

    preFilteredRows.forEach((row) => {
      const rowValue = row.values[id];

      if (rowValue === null || rowValue === undefined || String(rowValue).trim() === "") {
        hasEmpty = true;
      } else if (Array.isArray(rowValue)) {
        if (rowValue.length === 0) {
          hasEmpty = true;
        } else {
          rowValue.forEach((item) => {
            if (item === "" || item === null || item === undefined || String(item).trim() === "") {
              hasEmpty = true;
            } else {
              options.add(String(item));
            }
          });
        }
      } else {
        options.add(rowValue);
      }
    });

    const sortedOptions = [...options.values()].sort((a, b) =>
      String(a).localeCompare(String(b), "pt-BR"),
    );

    return { options: sortedOptions, hasEmpty };
  }, [id, preFilteredRows]);

  return (
    <Form.Select
      className="mt-2"
      value={filterValue ?? ""}
      onChange={(e) => {
        gotoPage?.(0);
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">{allOptionLabel !== undefined ? allOptionLabel : "Todos"}</option>
      {hasEmpty && (
        <option value="__EMPTY__">{emptyOptionLabel || "(Não informado)"}</option>
      )}
      {options.map((option, i) => (
        <option key={i} value={option}>
          {renderFilterLabel ? renderFilterLabel(option) : option}
        </option>
      ))}
    </Form.Select>
  );
}

export function generateExcelTemplate(platform, title, dataAddForm) {
  // Check if fields exist
  if (!dataAddForm?.fields || dataAddForm.fields.length === 0) {
    console.error("No fields found to generate template");
    return;
  }
  const validFields = dataAddForm.fields.filter((field) => (
    field.label
    && !field.hideFieldOnRegister
    && !(platform === 2 && String(field.label).trim().toLocaleLowerCase() === "email")
  ));
  let headers = [];

  // Extract headers from fields and add email as first column
  if (platform === 2) {
    headers.push("email"); // Add email as the first header for platform 2
  }

  headers = headers.concat(validFields.map((field) => field.label));

  // Header-only templates cannot be accidentally imported as a sample participant.
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  headers.forEach((header, index) => {
    const field = validFields.find((candidate) => candidate.label === header);
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: index })];
    if (!cell) return;
    const details = field
      ? [
        field.required || field.primary ? "Obrigatório" : "Opcional",
        `Validação: ${field.validation || "texto"}`,
        Array.isArray(field.options) && field.options.length > 0 ? `Opções: ${field.options.join(", ")}` : "",
        field.vacancyEnabled ? "A opção selecionada precisa ter vagas disponíveis." : "",
      ].filter(Boolean).join("\n")
      : "Obrigatório. Use um e-mail válido.";
    cell.c = [{ a: "R1 EvenTech", t: details }];
  });

  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

  // Generate the Excel file and trigger download
  XLSX.writeFile(workbook, `${title} - Modelo.xlsx`);
}

export const createImportValidationError = ({
  row = null,
  email = null,
  fieldId = null,
  fieldLabel = null,
  value = null,
  code = "IMPORT_VALIDATION",
  message = null,
  source = "file",
} = {}) => ({
  row,
  email,
  fieldId,
  fieldLabel,
  value,
  code,
  message: message || getImportValidationMessage({ code, fieldLabel, value }),
  source,
});

// Import API responses carry stable codes and context only. Keeping the
// operator-facing copy here makes the interface consistent and translatable
// without coupling it to server implementation messages.
export const getImportValidationMessage = ({ code, fieldLabel, field_label: serverFieldLabel, value } = {}) => {
  const resolvedFieldLabel = fieldLabel || serverFieldLabel;
  const field = resolvedFieldLabel ? ` no campo "${resolvedFieldLabel}"` : "";
  const option = value !== null && value !== undefined && String(value).trim() !== ""
    ? ` "${String(value).trim()}"`
    : "";
  const namedValue = option || (resolvedFieldLabel ? ` "${resolvedFieldLabel}"` : "");

  switch (code) {
    case "FILE_MISSING": return "Selecione um arquivo para importar.";
    case "FILE_TOO_LARGE": return "O arquivo excede o limite de 10 MB. Reduza-o e tente novamente.";
    case "FILE_TYPE": return "Use um arquivo .xlsx, .xls ou .csv.";
    case "FILE_READ": return "Não foi possível ler o arquivo. Feche-o em outros programas e tente novamente.";
    case "FILE_PARSE": return "O conteúdo do arquivo não pôde ser lido. Exporte-o novamente como .xlsx, .xls ou .csv.";
    case "MULTIPLE_SHEETS": return "O arquivo possui mais de uma planilha. Mantenha apenas uma para importar.";
    case "EMPTY_FILE": return "Inclua uma linha de cabeçalhos na primeira linha do arquivo.";
    case "EMPTY_HEADER": return "Preencha o cabeçalho desta coluna ou remova a coluna vazia.";
    case "DUPLICATE_HEADER": return `O cabeçalho${namedValue} está repetido. Renomeie ou remova uma das colunas.`;
    case "UNKNOWN_HEADER": return `O cabeçalho${namedValue} não pertence ao formulário atual. Corrija-o ou remova a coluna.`;
    case "MISSING_EMAIL_HEADER": return "Inclua a coluna obrigatória \"email\" no arquivo.";
    case "MISSING_REQUIRED_HEADER": return `Inclua a coluna obrigatória${namedValue}.`;
    case "PARTICIPANT_LOOKUP_FAILED": return "Não foi possível verificar participantes já cadastrados. Tente novamente antes de importar.";
    case "IMPORT_PREFLIGHT_FAILED": return "Não foi possível concluir a validação no servidor. Tente novamente.";
    case "FIELD_VALIDATION": return "Corrija o valor informado conforme a regra deste campo.";
    case "IMPORT_REQUEST_INVALID": return "Não foi possível validar a importação para este evento.";
    case "IMPORT_EVENT_JSON_INVALID": return "A configuração atual do evento é inválida. Atualize a página e tente novamente.";
    case "IMPORT_ROWS_INVALID": return "Os participantes importados precisam estar em uma lista válida.";
    case "IMPORT_ROW_INVALID": return "Esta linha possui um formato inválido.";
    case "IMPORT_RELATED_PARTICIPANT_INVALID": return "Um participante adicional possui um e-mail inválido.";
    case "AMBIGUOUS_EMAIL_HEADER": return "O título \"email\" é reservado para o e-mail do participante na Plataforma 2.";
    case "DUPLICATE_FORM_LABEL": return `Há mais de um campo do formulário com o título "${resolvedFieldLabel || String(value || "")}". Renomeie um deles antes de importar.`;
    case "INVALID_EMAIL": return "Informe um e-mail válido para o participante.";
    case "EMAIL_DOMAIN_NOT_ALLOWED": return "O domínio deste e-mail não é permitido para o evento.";
    case "DUPLICATE_EMAIL_IN_FILE": return "Este e-mail está repetido no arquivo.";
    case "DUPLICATE_EMAIL": return "Este e-mail já está cadastrado no evento.";
    case "DUPLICATE_PRIMARY_IN_FILE": return `Este valor de identificação está repetido no arquivo${field}.`;
    case "DUPLICATE_PRIMARY": return `Este valor de identificação já está cadastrado no evento${field}.`;
    case "INVALID_SUBSCRIPTION_LIMIT": return "O limite de inscrições do evento é inválido.";
    case "SUBSCRIPTION_LIMIT_EXHAUSTED": return "O limite total de inscrições do evento foi atingido.";
    case "INVALID_FORM_FIELD_VACANCY": return `A opção${option} não possui uma configuração de vagas válida${field}.`;
    case "FORM_FIELD_VACANCY_EXHAUSTED": return `A opção${option} não possui vagas disponíveis${field}.`;
    case "IMPORT_VALIDATION_FAILED": return "A importação possui erros que precisam ser corrigidos.";
    case "EVENT_UPDATE_FAILED": return "Não foi possível salvar o evento. Tente novamente.";
    default: return "Não foi possível validar esta informação da importação.";
  }
};

export const getImportValidationCategory = (code = "") => {
  switch (code) {
    case "FILE_MISSING":
    case "FILE_TOO_LARGE":
    case "FILE_TYPE":
    case "FILE_READ":
    case "FILE_PARSE":
    case "MULTIPLE_SHEETS":
    case "EMPTY_FILE": return "Arquivo";
    case "EMPTY_HEADER":
    case "DUPLICATE_HEADER":
    case "UNKNOWN_HEADER":
    case "MISSING_EMAIL_HEADER":
    case "MISSING_REQUIRED_HEADER":
    case "AMBIGUOUS_EMAIL_HEADER":
    case "DUPLICATE_FORM_LABEL": return "Cabeçalhos e formulário";
    case "FIELD_VALIDATION": return "Conteúdo do campo";
    case "INVALID_EMAIL":
    case "EMAIL_DOMAIN_NOT_ALLOWED": return "E-mail";
    case "DUPLICATE_EMAIL_IN_FILE":
    case "DUPLICATE_EMAIL":
    case "DUPLICATE_PRIMARY_IN_FILE":
    case "DUPLICATE_PRIMARY": return "Duplicidade";
    case "INVALID_FORM_FIELD_VACANCY":
    case "FORM_FIELD_VACANCY_EXHAUSTED": return "Vagas por opção";
    case "INVALID_SUBSCRIPTION_LIMIT":
    case "SUBSCRIPTION_LIMIT_EXHAUSTED": return "Limite de inscrições";
    case "IMPORT_REQUEST_INVALID":
    case "IMPORT_EVENT_JSON_INVALID":
    case "IMPORT_ROWS_INVALID":
    case "IMPORT_ROW_INVALID":
    case "IMPORT_RELATED_PARTICIPANT_INVALID":
    case "IMPORT_PREFLIGHT_FAILED":
    case "IMPORT_VALIDATION_FAILED":
    case "EVENT_UPDATE_FAILED": return "Validação do servidor";
    default: return "Outros";
  }
};

export const normalizeServerImportValidationError = (issue = {}, row = null) => createImportValidationError({
  row,
  email: issue.email || null,
  fieldId: issue.field_id || null,
  fieldLabel: issue.field_label || null,
  value: issue.value ?? null,
  code: issue.code || "IMPORT_VALIDATION_FAILED",
  message: getImportValidationMessage(issue),
  source: "server",
});

export const getImportValidationSummary = (errors = []) => (
  errors.length === 1
    ? "Foi encontrado 1 erro na importação. Corrija-o antes de salvar."
    : `Foram encontrados ${errors.length} erros na importação. Corrija-os antes de salvar.`
);

export const getImportValidationToast = (errors = [], fallbackCode = "IMPORT_VALIDATION_FAILED") => {
  if (errors.length === 1) return errors[0].message || getImportValidationMessage(errors[0]);
  if (errors.length > 1) return `${getImportValidationSummary(errors)} Consulte o relatório para ver cada linha.`;
  return getImportValidationMessage({ code: fallbackCode });
};

export const createImportValidationReport = (errors = [], summary = "") => ({
  open: true,
  summary: summary || getImportValidationSummary(errors),
  errors,
});

export const setImportValidationReport = (setDataAddForm, errors, summary) => {
  const report = createImportValidationReport(errors, summary);
  setDataAddForm((prevState) => ({
    ...prevState,
    importUsers: {
      ...prevState.importUsers,
      users: [],
      fields: [],
      error: report.summary,
      errorList: errors,
      report,
    },
  }));
};

// validationConfig (optional): { fields, event_id, platform, validEmailsConfig }
export const handleFileUpload = (
  platform,
  event,
  dataAddForm,
  setDataAddForm,
  importUsersInput,
  validationConfig = null,
) => {
  const file = event.target.files[0];

  // Reset any previous errors
  setDataAddForm((prevState) => ({
    ...prevState,
    importUsers: {
      ...prevState.importUsers,
      users: [],
      fields: [],
      error: "",
      errorList: [],
      report: null,
      formValue: "",
      formFilename: "",
    },
  }));

  // Check if the file exists
  if (!file) {
    setImportValidationReport(setDataAddForm, [createImportValidationError({ message: "Nenhum arquivo encontrado", code: "FILE_MISSING" })], "Nenhum arquivo encontrado.");
    return;
  }

  // Check file size (optional, e.g., max 2MB)
  if (file.size > 10 * 1024 * 1024) {
    setImportValidationReport(setDataAddForm, [createImportValidationError({ message: "Arquivo maior que 10 MB", code: "FILE_TOO_LARGE" })], "O arquivo excede o limite de 10 MB.");
    return;
  }

  // Check file type
  const allowedTypes = [".xlsx", ".xls", ".csv"];
  const fileType = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  if (!allowedTypes.includes(fileType)) {
    setImportValidationReport(setDataAddForm, [createImportValidationError({ message: "Formato inválido. Use .xlsx, .xls ou .csv.", code: "FILE_TYPE" })], "Formato de arquivo não suportado.");
    return;
  }

  const reader = new FileReader();

  reader.onload = (e) => {
    // Use an async IIFE so we can await the Yup schema validation mid-read
    (async () => {
      try {
        const fail = (errors, summary) => {
          setImportValidationReport(setDataAddForm, errors, summary);
          importUsersInput.current.value = "";
        };
        const arrayBuffer = e.target.result;

        // Parse the Excel/CSV file
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        if (workbook.SheetNames.length > 1) {
          fail([createImportValidationError({ message: "Existem múltiplas folhas dentro do arquivo. Mantenha apenas uma.", code: "MULTIPLE_SHEETS" })], "O arquivo contém mais de uma folha.");
          return;
        }

        // Get the first sheet name
        const sheetName = workbook.SheetNames[0];

        // Get data from the first sheet
        const sheet = workbook.Sheets[sheetName];

        // Keep the array row index so reports reference the physical Excel row.
        const data = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: null,
          blankrows: true,
        });

        // Extract column titles from the first row and ensure they are strings
        const headers = (data[0] || []).map(h => h != null ? String(h).trim() : h);

        const parsedData = data.slice(1).map((cells, rowOffset) => {
          if (!Array.isArray(cells) || cells.every((value) => value === null || value === undefined || String(value).trim() === "")) {
            return null;
          }
          const newRow = {};
          headers.forEach((header, columnIndex) => {
            if (!header) return;
            const stringKey = String(header).trim();
            const val = cells[columnIndex];
            const matched = dataAddForm.fields.find((field) => String(field.label).trim() === stringKey);
            if (matched && matched.type === "checkbox" && matched.allowMultipleChoice && typeof val === "string") {
              try {
                // Handle unescaped or escaped CSV quotes safely
                let cleanVal = val;
                if (cleanVal.startsWith('"[') && cleanVal.endsWith(']"')) {
                  // Sometimes CSV wraps the json string in extra quotes
                  cleanVal = cleanVal.slice(1, -1).replace(/""/g, '"');
                }
                newRow[stringKey] = JSON.parse(cleanVal);
              } catch (e) {
                // If parsing fails, fall back to the raw string
                newRow[stringKey] = val;
              }
            } else {
              newRow[stringKey] = val;
            }
          });
          Object.defineProperty(newRow, "__importRow", { value: rowOffset + 2, enumerable: false });
          return newRow;
        }).filter(Boolean);

        // Spreadsheet labels are a public import contract. Report every structural
        // problem at once instead of stopping at the first bad header.
        const newFields = [];
        const headerErrors = [];
        const importableFields = dataAddForm.fields.filter((field) => field.label && !field.hideFieldOnRegister);
        const fieldByLabel = new Map();
        const ambiguousFormLabels = new Set();
        importableFields.forEach((field) => {
          const label = String(field.label).trim();
          if (platform === 2 && label.toLocaleLowerCase() === "email") {
            headerErrors.push(createImportValidationError({
              fieldId: field.inputID,
              fieldLabel: label,
              code: "AMBIGUOUS_EMAIL_HEADER",
              message: 'O título "email" é reservado para o e-mail do participante na Plataforma 2.',
            }));
            return;
          }
          if (fieldByLabel.has(label)) {
            ambiguousFormLabels.add(label);
            headerErrors.push(createImportValidationError({
              fieldId: field.inputID,
              fieldLabel: label,
              code: "DUPLICATE_FORM_LABEL",
              message: `O formulário possui mais de um campo com o título "${label}". Renomeie os campos antes de importar.`,
            }));
          } else {
            fieldByLabel.set(label, field);
          }
        });

        const seenHeaders = new Set();
        let confirmEmail = false;
        if (headers.length === 0) {
          headerErrors.push(createImportValidationError({ code: "EMPTY_FILE", message: "O arquivo não possui uma linha de cabeçalhos." }));
        }
        headers.forEach((element, columnIndex) => {
          const header = element === undefined || element === null ? "" : String(element).trim();
          if (!header) {
            headerErrors.push(createImportValidationError({ code: "EMPTY_HEADER", message: `A coluna ${columnIndex + 1} não possui cabeçalho.` }));
            return;
          }
          if (seenHeaders.has(header)) {
            headerErrors.push(createImportValidationError({ fieldLabel: header, code: "DUPLICATE_HEADER", message: `O cabeçalho "${header}" está repetido.` }));
            return;
          }
          seenHeaders.add(header);
          if (platform === 2 && header === "email") {
            confirmEmail = true;
            newFields.push({ Header: header, accessor: header, disableSortBy: true, Filter: false });
            return;
          }
          const matchedField = fieldByLabel.get(header);
          if (!matchedField) {
            headerErrors.push(createImportValidationError({ fieldLabel: header, code: "UNKNOWN_HEADER", message: `O cabeçalho "${header}" não corresponde a um campo disponível para inscrição.` }));
            return;
          }
          newFields.push({ Header: header, accessor: header, disableSortBy: true, Filter: false });
        });

        const headerCounts = new Map();
        headers.forEach((element) => {
          const header = element === undefined || element === null ? "" : String(element).trim();
          headerCounts.set(header, (headerCounts.get(header) || 0) + 1);
        });

        if (platform === 2 && !confirmEmail) {
          headerErrors.push(createImportValidationError({ fieldLabel: "email", code: "MISSING_EMAIL_HEADER", message: 'O cabeçalho "email" é obrigatório no arquivo.' }));
        }
        importableFields.filter((field) => {
          const hasDependencies = Array.isArray(field.dependencies)
            ? field.dependencies.length > 0
            : Boolean(field.dependencies?.inputID);
          return (field.required || field.primary) && !hasDependencies;
        }).forEach((field) => {
          const label = String(field.label).trim();
          if (!seenHeaders.has(label)) {
            headerErrors.push(createImportValidationError({ fieldId: field.inputID, fieldLabel: label, code: "MISSING_REQUIRED_HEADER", message: `O cabeçalho obrigatório "${label}" está ausente.` }));
          }
        });
        // Normal imports validate every registration-form rule. The emergency
        // mode keeps the Platform 2 e-mail rule, but bypasses the other browser
        // field checks; server-side checks always remain active.
        if (validationConfig) {
          const {
            fields: vcFields,
            event_id: vcEventId,
            platform: vcPlatform,
            validEmailsConfig: vcValidEmails,
          } = validationConfig;
          const bypassNormalRowValidation = Boolean(validationConfig.skipRowValidation);

          const allErrors = [...headerErrors];

          // A malformed header must not prevent independent row checks. Only
          // validate fields whose label maps to exactly one spreadsheet column,
          // exactly one form field, and whose dependency chain is also mapped.
          const uniquelyMappedFields = vcFields.filter((field) => {
            const label = String(field.label || "").trim();
            return label
              && !field.hideFieldOnRegister
              && !ambiguousFormLabels.has(label)
              && headerCounts.get(label) === 1
              && String(fieldByLabel.get(label)?.inputID) === String(field.inputID);
          });
          const uniquelyMappedFieldIds = new Set(uniquelyMappedFields.map((field) => String(field.inputID)));
          const hasMappedDependencyChain = (field, visited = new Set()) => {
            const fieldId = String(field.inputID);
            if (visited.has(fieldId)) return false;
            const nextVisited = new Set(visited);
            nextVisited.add(fieldId);
            const dependencies = Array.isArray(field.dependencies)
              ? field.dependencies
              : field.dependencies?.inputID ? [field.dependencies] : [];
            return dependencies.every((dependency) => {
              if (!dependency?.inputID) return true;
              const parent = vcFields.find((candidate) => String(candidate.inputID) === String(dependency.inputID));
              return Boolean(parent)
                && uniquelyMappedFieldIds.has(String(parent.inputID))
                && hasMappedDependencyChain(parent, nextVisited);
            });
          };
          const validationFields = bypassNormalRowValidation
            ? []
            : uniquelyMappedFields.filter((field) => hasMappedDependencyChain(field));
          const primaryFields = bypassNormalRowValidation
            ? []
            : validationFields.filter((field) => field.primary);
          const hasValidEmailHeader = vcPlatform === 2
            && headerCounts.get("email") === 1
            && !headerErrors.some((error) => error.code === "AMBIGUOUS_EMAIL_HEADER");

          // Build the registration-form schema before any duplicate or capacity
          // work. Header errors stay in the report, while this pass validates
          // every field that can be mapped without ambiguity.
          const importSchema = buildImportFormSchema(
            validationFields,
            vcPlatform,
            vcValidEmails,
            vcFields,
            hasValidEmailHeader,
          );

          // Pass 1: normalize each row and collect every browser field error.
          // Duplicate checks intentionally wait until this pass is complete so
          // malformed values cannot produce misleading duplicate errors.
          const rowValidationStates = [];
          for (const row of parsedData) {
            const validationObject = {};
            const fieldErrors = new Set();

            validationFields.forEach((matchedField) => {
              const key = matchedField.label;
              // Since we pre-parsed array strings in parsedData, we can assign directly.
              let valueToSet = row[key];

              // Fallbacks for checkboxes or missing things.
              if (valueToSet === undefined || valueToSet === null) {
                valueToSet = "";
              } else if (matchedField.type === "checkbox" && !matchedField.allowMultipleChoice) {
                valueToSet = String(valueToSet);
              } else if (typeof valueToSet !== "object") {
                valueToSet = String(valueToSet);
              }

              validationObject[matchedField.inputID] = valueToSet;
            });
            if (hasValidEmailHeader) validationObject.email = row.email;

            if (Object.keys(validationObject).length > 0) {
              try {
                await importSchema.validate(validationObject, { abortEarly: false });
              } catch (yupError) {
                if (yupError.inner && yupError.inner.length > 0) {
                  yupError.inner.forEach((e) => {
                    const errorPath = e.path || "__general__";
                    fieldErrors.add(String(errorPath));
                    const field = vcFields.find((f) => f.inputID?.toString() === e.path);
                    allErrors.push(createImportValidationError({
                      row: row.__importRow,
                      email: vcPlatform === 2 ? String(row.email || "").trim() || null : null,
                      fieldId: field?.inputID || e.path || null,
                      fieldLabel: field?.label || e.path || null,
                      value: field ? row[field.label] : e.path === "email" ? row.email : null,
                      code: "FIELD_VALIDATION",
                      message: e.message,
                    }));
                  });
                } else {
                  fieldErrors.add("__general__");
                  allErrors.push(createImportValidationError({ row: row.__importRow, code: "FIELD_VALIDATION", message: yupError.message }));
                }
              }
            }
            rowValidationStates.push({ row, validationObject, fieldErrors });
          }

          // Pass 2: load existing participants once, but only when at least one
          // valid value can actually participate in a duplicate check.
          let existingEmailsSet = new Set();
          const existingPrimaryValues = new Map();
          primaryFields.forEach((field) => existingPrimaryValues.set(String(field.inputID), new Set()));

          const hasDuplicateCandidate = headerErrors.length === 0 && rowValidationStates.some(({ row, validationObject, fieldErrors }) => {
            const hasValidEmail = vcPlatform === 2
              && !fieldErrors.has("email")
              && String(row.email || "").trim() !== "";
            const hasValidPrimary = primaryFields.some((field) => {
              const fieldId = String(field.inputID);
              const value = validationObject[field.inputID];
              return !fieldErrors.has(fieldId)
                && value !== undefined
                && value !== null
                && String(value).trim() !== "";
            });
            return hasValidEmail || hasValidPrimary;
          });

          if (!bypassNormalRowValidation && hasDuplicateCandidate) {
            try {
              const res = await axios.get(
                `/api/event/participants/${vcEventId}?platform=${vcPlatform}`,
              );
              const raw = res.data?.participants ?? res.data?.message ?? [];
              if (Array.isArray(raw)) {
                raw.forEach((p) => {
                  if (vcPlatform === 2 && p.email) {
                    existingEmailsSet.add(String(p.email).toLowerCase().trim());
                  }
                  if (p.json && primaryFields.length > 0) {
                    const parsedJson = typeof p.json === "string" ? safeJsonParse(p.json) : p.json;
                    primaryFields.forEach((field) => {
                      const primaryValue = getJsonFieldValue(parsedJson?.[field.inputID]);
                      if (primaryValue !== undefined && primaryValue !== null && String(primaryValue).trim() !== "") {
                        existingPrimaryValues.get(String(field.inputID)).add(String(primaryValue).toLowerCase().trim());
                      }
                    });
                  }
                });
              }
            } catch (e) {
              allErrors.push(createImportValidationError({
                code: "PARTICIPANT_LOOKUP_FAILED",
                message: "Não foi possível validar participantes já cadastrados. Tente novamente antes de importar.",
              }));
            }
          }

          // Pass 3: duplicate checks run only on values that passed their own
          // field validation. This keeps duplicate feedback actionable.
          const sheetEmailsSeen = new Map();
          const reportedFileEmailDuplicates = new Set();
          const sheetPrimarySeen = new Map();
          const reportedFilePrimaryDuplicates = new Set();
          primaryFields.forEach((field) => sheetPrimarySeen.set(String(field.inputID), new Map()));

          if (headerErrors.length === 0) rowValidationStates.forEach(({ row, validationObject, fieldErrors }) => {
            if (vcPlatform === 2 && !bypassNormalRowValidation && !fieldErrors.has("email")) {
              const email = String(row.email || "").trim();
              if (email) {
                const emailLower = email.toLowerCase();
                if (sheetEmailsSeen.has(emailLower)) {
                  const firstRow = sheetEmailsSeen.get(emailLower);
                  if (!reportedFileEmailDuplicates.has(firstRow)) {
                    reportedFileEmailDuplicates.add(firstRow);
                    allErrors.push(createImportValidationError({ row: firstRow, email, fieldLabel: "email", value: email, code: "DUPLICATE_EMAIL_IN_FILE", message: "E-mail duplicado no arquivo" }));
                  }
                  allErrors.push(createImportValidationError({ row: row.__importRow, email, fieldLabel: "email", value: email, code: "DUPLICATE_EMAIL_IN_FILE", message: "E-mail duplicado no arquivo" }));
                } else {
                  sheetEmailsSeen.set(emailLower, row.__importRow);
                }
                if (existingEmailsSet.has(emailLower)) {
                  allErrors.push(createImportValidationError({ row: row.__importRow, email, fieldLabel: "email", value: email, code: "DUPLICATE_EMAIL", message: "E-mail já cadastrado no evento" }));
                }
              }
            }

            primaryFields.forEach((primaryField) => {
              const fieldId = String(primaryField.inputID);
              if (fieldErrors.has(fieldId)) return;
              const val = validationObject[primaryField.inputID];
              if (val !== undefined && val !== null && String(val).trim() !== "") {
                const valLower = String(val).toLowerCase().trim();
                const seenValues = sheetPrimarySeen.get(fieldId);
                const registeredValues = existingPrimaryValues.get(fieldId);

                if (seenValues.has(valLower)) {
                  const duplicateKey = `${fieldId}:${valLower}`;
                  const firstRow = seenValues.get(valLower);
                  if (!reportedFilePrimaryDuplicates.has(duplicateKey)) {
                    reportedFilePrimaryDuplicates.add(duplicateKey);
                    allErrors.push(createImportValidationError({ row: firstRow, fieldId: primaryField.inputID, fieldLabel: primaryField.label, value: val, code: "DUPLICATE_PRIMARY_IN_FILE", message: "Valor de identificação duplicado no arquivo" }));
                  }
                  allErrors.push(createImportValidationError({ row: row.__importRow, fieldId: primaryField.inputID, fieldLabel: primaryField.label, value: val, code: "DUPLICATE_PRIMARY_IN_FILE", message: "Valor de identificação duplicado no arquivo" }));
                } else {
                  seenValues.set(valLower, row.__importRow);
                }
                if (registeredValues.has(valLower)) {
                  allErrors.push(createImportValidationError({ row: row.__importRow, fieldId: primaryField.inputID, fieldLabel: primaryField.label, value: val, code: "DUPLICATE_PRIMARY", message: `${primaryField.label} já registrado` }));
                }
              }
            });
          });

          if (allErrors.length > 0) {
            fail(allErrors, `Foram encontrados ${allErrors.length} erros no arquivo.`);
            return;
          }
        }
        if (!validationConfig && headerErrors.length > 0) {
          fail(headerErrors, `Foram encontrados ${headerErrors.length} problemas nos cabeçalhos do arquivo.`);
          return;
        }
        if (validationConfig?.eventJson) {
          try {
            const preflight = await axios.post(`/api/event/${validationConfig.event_id}/import/validate`, {
              platform: validationConfig.platform,
              json: JSON.stringify(validationConfig.eventJson),
              participants: parsedData,
            });
            const serverErrors = Array.isArray(preflight.data?.errors) ? preflight.data.errors : [];
            if (serverErrors.length > 0) {
              const errors = serverErrors.map((error) => (
                normalizeServerImportValidationError(
                  error,
                  parsedData[Number(error.row_index)]?.__importRow || null,
                )
              ));
              fail(errors, getImportValidationSummary(errors));
              return;
            }
          } catch (preflightError) {
            const serverErrors = preflightError?.response?.data?.errors;
            if (Array.isArray(serverErrors) && serverErrors.length > 0) {
              const errors = serverErrors.map((error) => (
                normalizeServerImportValidationError(
                  error,
                  parsedData[Number(error.row_index)]?.__importRow || null,
                )
              ));
              fail(errors, getImportValidationSummary(errors));
            } else {
              const code = preflightError?.response?.data?.code || "IMPORT_VALIDATION_FAILED";
              fail(
                [createImportValidationError({ code, message: getImportValidationMessage({ code }), source: "server" })],
                "A validação da importação não pôde ser concluída.",
              );
            }
            return;
          }
        }
        // ──────────────────────────────────────────────────────────────────────

        setDataAddForm((prevState) => ({
          ...prevState,
          importUsers: {
            ...prevState.importUsers,
            fields: newFields,
            users: parsedData,
            error: "",
            errorList: [],
            report: null,
            formFilename: file.name,
          },
        }));
        importUsersInput.current.value = "";
      } catch (error) {
        setImportValidationReport(setDataAddForm, [createImportValidationError({ code: "FILE_PARSE", message: "Conteúdo do arquivo com formato inválido" })], "Não foi possível ler o arquivo.");
        importUsersInput.current.value = "";
      }
    })();
  };

  reader.onerror = () => {
    setImportValidationReport(setDataAddForm, [createImportValidationError({ code: "FILE_READ", message: "Erro ao ler o arquivo, tente novamente" })], "Não foi possível ler o arquivo.");
    importUsersInput.current.value = "";
  };

  reader.readAsArrayBuffer(file);
};

export const useInfiniteScroll = (
  scrollContainerRef,
  dispatch,
  setCurrentPage,
  selectEventsData,
) => {
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // Only setup infinite scroll if there are 15 or more events
    if (!selectEventsData.value || selectEventsData.value.length < 15) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          !selectEventsData.firstFetch &&
          !selectEventsData.stopFetching &&
          Array.isArray(selectEventsData.value) &&
          selectEventsData.value.length > 0
        ) {
          if (
            entries[0].isIntersecting &&
            selectEventsData.pageFetching === false
          ) {
            dispatch(setCurrentPage());
          }
        }
      },
      {
        root: scrollContainer,
        rootMargin: "25px",
        threshold: 1.0,
      },
    );

    const sentinel = document.createElement("div");
    scrollContainer.appendChild(sentinel);
    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
      sentinel.remove(); // safer than removeChild — won't throw if already detached
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scrollContainerRef,
    selectEventsData.firstFetch,
    selectEventsData.pageFetching,
    selectEventsData.stopFetching,
    selectEventsData.value?.length,
  ]);
};

export function generatePassword(length) {
  var result = "";
  var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function generateSixDigitCode() {
  let code = "";
  for (let i = 0; i < 6; i++) {
    const randomDigit = Math.floor(Math.random() * 10); // Generate a random digit between 0 and 9
    code += randomDigit;
  }
  return parseInt(code);
}

// Utility function to strip units from the value
export const stripUnit = (value) => value.replace(/(px|rem)$/, "");

// Utility function to append units to the value
export const appendUnit = (value, unit) => `${value}${unit}`;

export const returnDrawType = (value) => {
  switch (value) {
    case `DrawDefault`:
      return "Apenas participantes";
    case `DrawName`:
      return "Sorteio por nomes";
    case `DrawNumber`:
      return "Sorteio por números";

    default:
      return "";
  }
};

export function SocialMidiaIcons({ element, index }) {
  let iconMedia;
  let url;
  switch (element["name"]) {
    case "facebook":
      iconMedia = faFacebook;
      url = element["url"];
      break;
    case "instagram":
      iconMedia = faInstagram;
      url = element["url"];
      break;
    case "youtube":
      iconMedia = faYoutube;
      url = element["url"];
      break;
    case "linkedin":
      iconMedia = faLinkedin;
      url = element["url"];
      break;
    case "twitter":
      iconMedia = faTwitter;
      url = element["url"];
      break;
    case "website":
      iconMedia = faGlobe;
      url = element["url"];
      break;
    case "whatsapp":
      iconMedia = faWhatsapp;
      url = element["url"];
      break;
    default:
      break;
  }
  return (
    <FontAwesomeIcon
      key={index + "icon"}
      icon={iconMedia}
      className="mx-2 iconsHover"
      size={"xl"}
      onClick={() => redirectURL(true, url)}
    />
  );
}

export const isFieldVisible = (field, allFields, currentValues, visited = new Set(), ignoreHideOnRegister = false) => {
  if (!field) return false;

  if (visited.has(field.inputID)) {
    console.warn(`Circular dependency detected for field ${field.inputID}`);
    return false;
  }

  // Create a new set for this path to avoid modifying the caller's set
  const currentVisited = new Set(visited);
  currentVisited.add(field.inputID);

  if (!ignoreHideOnRegister && field.hideFieldOnRegister) {
    return false;
  }

  if (
    field.dependencies &&
    Array.isArray(field.dependencies) &&
    field.dependencies.length > 0
  ) {
    return field.dependencies.every((dependency) => {
      if (!dependency.inputID || dependency.inputID === "") {
        return true;
      }

      const parentField = allFields.find(
        (f) => f.inputID.toString() === dependency.inputID.toString()
      );

      if (
        parentField &&
        !isFieldVisible(parentField, allFields, currentValues, currentVisited, ignoreHideOnRegister)
      ) {
        return false;
      }

      const dependencyValue = currentValues[dependency.inputID];

      if (Array.isArray(dependencyValue)) {
        return dependencyValue.includes(dependency.value);
      } else {
        // eslint-disable-next-line eqeqeq
        return dependencyValue == dependency.value;
      }
    });
  } else if (
    field.dependencies &&
    field.dependencies.inputID !== "" &&
    field.dependencies.inputID !== undefined
  ) {
    // legacy single dependency
    const parentField = allFields.find(
      (f) => f.inputID.toString() === field.dependencies.inputID.toString()
    );
    if (
      parentField &&
      !isFieldVisible(parentField, allFields, currentValues, currentVisited, ignoreHideOnRegister)
    ) {
      return false;
    }

    const dependencyValue = currentValues[field.dependencies.inputID];
    if (Array.isArray(dependencyValue)) {
      return dependencyValue.includes(field.dependencies.value);
    } else {
      // eslint-disable-next-line eqeqeq
      return dependencyValue == field.dependencies.value;
    }
  }

  return true;
};

// Bulk imports use the registration-form validators without their per-field
// network calls. Duplicate checks are performed in one bulk request instead.
export function buildImportFormSchema(
  fields,
  platform,
  validEmailsConfig = null,
  allFields = fields,
  includePlatformEmail = true,
) {
  const json = {};
  fields.forEach((field) => {
    json[field.inputID.toString()] = Yup.mixed().test("relatedFields", function (value) {
      if (!isFieldVisible(field, allFields, this.parent, new Set())) return true;
      try {
        const validator = switchValidation(field, null, validEmailsConfig, { skipDuplicate: true });
        if (!validator || typeof validator.validateSync !== "function") return true;
        validator.validateSync(value, { abortEarly: true, context: { parent: this.parent } });
        return true;
      } catch (error) {
        return this.createError({ message: error?.errors?.[0] || error?.message || "Erro de validação" });
      }
    });
  });
  if (platform === 2 && includePlatformEmail) {
    json.email = Yup.string()
      .required("Campo obrigatório")
      .email("E-mail inválido")
      .test("email-domain", function () {
        return checkEmailDomain(this, validEmailsConfig);
      });
  }
  return Yup.object().shape(json);
}

export function schemaCreator(
  fields,
  event_id,
  editValues,
  platform,
  showFullForm,
  validEmailsConfig = null,
  ignoreHideOnRegister = false,
) {
  let json = {};
  if ((showFullForm && platform === 2) || platform === 1) {
    fields.forEach((value, key) => {
      let id = value.inputID.toString();
      json[id] = getValidator(value, event_id, validEmailsConfig, fields, ignoreHideOnRegister);
    });
  }

  if (platform === 2) {
    json["email"] = Yup.string()
      .required("Campo obrigatório")
      .email("E-mail inválido")
      .test("email-domain", function (value) {
        return checkEmailDomain(this, validEmailsConfig);
      })
      .test("email-duplicate", function (value) {
        return new Promise((resolve, reject) => {
          if (editValues) {
            if (this.originalValue !== editValues.email) {
              resolve(checkEmail(this, event_id));
            } else {
              resolve(true);
            }
          } else {
            resolve(checkEmail(this, event_id));
          }
        });
      });
  }
  return Yup.object().shape(json);
}

function getNormalizedFieldOptions(field) {
  if (!Array.isArray(field?.options)) return [];
  return field.options
    .map((opt) => (opt === undefined || opt === null ? "" : String(opt).trim()))
    .filter((opt) => opt !== "");
}

function validateMultiChoiceSelection(valueX, field, createError) {
  if (!Array.isArray(valueX)) {
    return createError({
      message: "Formato inválido: este campo aceita uma lista de opções",
    });
  }

  const validOptions = getNormalizedFieldOptions(field);
  if (validOptions.length === 0) return true;

  const invalidOptions = valueX
    .map((opt) => (opt === undefined || opt === null ? "" : String(opt).trim()))
    .filter((opt) => opt !== "" && !validOptions.includes(opt));

  if (invalidOptions.length > 0) {
    return createError({
      message: `Opção inválida: ${invalidOptions.join(", ")}`,
    });
  }

  return true;
}

function validateSingleChoiceSelection(valueX, field, createError) {
  const validOptions = getNormalizedFieldOptions(field);
  if (validOptions.length === 0) return true;

  const normalizedValue =
    valueX === undefined || valueX === null ? "" : String(valueX).trim();

  if (normalizedValue === "") return true;

  if (!validOptions.includes(normalizedValue)) {
    return createError({
      message: `Opção inválida: ${normalizedValue}`,
    });
  }

  return true;
}

// Backward-compatible alias for callers outside the current importer. The
// registration validators are now defined in one shared builder above.
export const buildSyncImportSchema = buildImportFormSchema;

/* Retained temporarily for source-history readability; this old copy is not
 * executable and is superseded by buildImportFormSchema.
export function legacyBuildSyncImportSchema(fields, platform, validEmailsConfig = null) {
  const json = {};

  // Build per-field validators (same logic as switchValidation but sync-only)
  const buildFieldValidator = (field) => {
    switch (field.validation) {
      case "text":
      case "textBig": {
        const maxLen = field.validation === "textBig" ? "2500" : "255";
        const maxMsg = field.validation === "textBig" ? "Máximo de 2500 caracteres" : "Máximo de 255 caracteres";
        if (field.required || field.primary) {
          return Yup.string().required("Campo obrigatório").max(maxLen, maxMsg);
        }
        return Yup.string().max(maxLen, maxMsg);
      }
      case "email": {
        let v = Yup.string().max("255", "Máximo de 255 caracteres").email("E-mail inválido (Formato esperado: usuario@dominio.com)");
        if (field.required || field.primary) v = v.required("Campo obrigatório (Formato esperado: usuario@dominio.com)");
        return v.test("email-domain", function (val) {
          return checkEmailDomain(this, validEmailsConfig);
        });
      }
      case "cpf": {
        let v = Yup.string().max("255", "Máximo de 255 caracteres");
        if (field.required || field.primary) {
          v = v.required("Campo obrigatório (Formato esperado: 123.456.789-10)").test("CPF", "CPF inválido (Formato esperado: 123.456.789-10)", (val) => !!validarCPF(val));
        }
        return v;
      }
      case "cnpj": {
        let v = Yup.string().max("255", "Máximo de 255 caracteres");
        if (field.required || field.primary) {
          v = v.required("Campo obrigatório (Formato esperado: 12.345.678/0001-95)");
        }
        return v.test("CNPJ", "CNPJ inválido (Formato esperado: 12.345.678/0001-95)", (val) => !val || !!validarCNPJ(val));
      }
      case "telefone": {
        let v = Yup.string().max("255", "Máximo de 255 caracteres");
        if (field.required || field.primary) {
          v = v.required("Campo obrigatório (Formato esperado: (11) 98765-4321)").test("telefone", "Telefone inválido (Formato esperado: (11) 98765-4321)", (val) =>
            countStandAloneNumbers(val ?? "").length > 9,
          );
        }
        return v;
      }
      case "date": {
        let v = Yup.string().max("255", "Máximo de 255 caracteres");
        if (field.required || field.primary) {
          v = v.required("Campo obrigatório (Formato esperado: DD/MM/AAAA ou DD/MM/AAAA HH:mm)").test("date", "Data inválida (Formato esperado: DD/MM/AAAA ou DD/MM/AAAA HH:mm)", (val) => validateDate(val));
        } else {
          v = v.test("date", "Data inválida (Formato esperado: DD/MM/AAAA ou DD/MM/AAAA HH:mm)", (val) => !val || validateDate(val));
        }
        return v;
      }
      case "number": {
        let v = Yup.number().typeError("Valor inválido, apenas números").test(
          "number", "Máximo de 255 caracteres", (val) => !val || val.toString().length <= 255,
        );
        if (field.required || field.primary) v = v.required("Campo obrigatório (Apenas números)");
        return v;
      }
      case "checkbox":
        if (field.allowMultipleChoice) {
          if (field.required) {
            return Yup.array().test("required", "", function (valueX) {
              if (
                valueX === undefined ||
                valueX === "" ||
                valueX === "false" ||
                valueX === false ||
                !Array.isArray(valueX) ||
                valueX.length === 0
              ) {
                return this.createError({
                  message: "Campo obrigatório (Formato esperado: [\"Opção 1\", \"Opção 2\"])",
                });
              } else {
                if (
                  field.allowMultipleChoiceMin &&
                  valueX.length < field.allowMultipleChoiceMin
                ) {
                  return this.createError({
                    message: `Selecione no mínimo ${field.allowMultipleChoiceMin} alternativas (Formato esperado: ["Opção 1", "Opção 2"])`,
                  });
                } else if (
                  field.allowMultipleChoiceMax &&
                  valueX.length > field.allowMultipleChoiceMax
                ) {
                  return this.createError({
                    message: `Limite de ${field.allowMultipleChoiceMax} alternativas (Formato esperado: ["Opção 1", "Opção 2"])`,
                  });
                } else {
                  return validateMultiChoiceSelection(valueX, field, (error) =>
                    this.createError(error),
                  );
                }
              }
            });
          } else {
            return Yup.array().test("required", "", function (valueX) {
              if (
                valueX === undefined ||
                valueX === null ||
                valueX === "" ||
                valueX === false ||
                valueX === "false"
              ) {
                return true;
              }

              if (!Array.isArray(valueX)) {
                return this.createError({
                  message: "Formato inválido: este campo aceita uma lista de opções",
                });
              }

              if (
                field.allowMultipleChoiceMin &&
                (valueX ? valueX.length : 0) < field.allowMultipleChoiceMin
              ) {
                return this.createError({
                  message: `Selecione no mínimo ${field.allowMultipleChoiceMin} alternativas (Formato esperado: ["Opção 1", "Opção 2"])`,
                });
              } else if (
                field.allowMultipleChoiceMax &&
                (valueX ? valueX.length : 0) > field.allowMultipleChoiceMax
              ) {
                return this.createError({
                  message: `Limite de ${field.allowMultipleChoiceMax} alternativas (Formato esperado: ["Opção 1", "Opção 2"])`,
                });
              } else {
                return validateMultiChoiceSelection(valueX, field, (error) =>
                  this.createError(error),
                );
              }
            });
          }
        } else {
          if (field.required) {
            return Yup.string()
              .nullable()
              .test("required", "", function (valueX) {
                if (
                  valueX === undefined ||
                  valueX === "" ||
                  valueX === "false" ||
                  valueX === false ||
                  valueX === null
                ) {
                  return this.createError({
                    message: "Campo obrigatório",
                  });
                } else {
                  return validateSingleChoiceSelection(valueX, field, (error) =>
                    this.createError(error),
                  );
                }
              });
          } else {
            return Yup.string()
              .nullable()
              .test("valid-option", "", function (valueX) {
                return validateSingleChoiceSelection(valueX, field, (error) =>
                  this.createError(error),
                );
              });
          }
        }
      default:
        return field.required || field.primary
          ? Yup.string().required("Campo obrigatório")
          : Yup.string();
    }
  };

  if (platform === 1) {
    // Platform 1: validate all form fields
    fields.forEach((field) => {
      json[field.inputID.toString()] = buildFieldValidator(field);
    });
  } else if (platform === 2) {
    // Platform 2: validate form fields + dedicated email field
    fields.forEach((field) => {
      json[field.inputID.toString()] = buildFieldValidator(field);
    });
    json["email"] = Yup.string()
      .required("Campo obrigatório (Formato esperado: usuario@dominio.com)")
      .email("E-mail inválido (Formato esperado: usuario@dominio.com)")
      .test("email-domain", function (val) {
        return checkEmailDomain(this, validEmailsConfig);
      });
  }

  return Yup.object().shape(json);
}
*/

function getValidator(value, event_id, validEmailsConfig = null, allFields = [], ignoreHideOnRegister = false) {
  return Yup.mixed().test("relatedFields", async function (val) {
    if (!isFieldVisible(value, allFields, this.parent, new Set(), ignoreHideOnRegister)) {
      return true; // Skip validation if not visible
    }

    try {
      await switchValidation(value, event_id, validEmailsConfig).validate(val, {
        abortEarly: true,
        context: { parent: this.parent },
      });
      return true;
    } catch (err) {
      return this.createError({
        message: err?.errors?.[0] || err?.message || "Erro de validação",
      });
    }
  });
}

function checkEmailDomain(props, validEmailsConfig) {
  const result = validateEmailDomain(props?.originalValue, { json: { form: { validEmails: validEmailsConfig } } });

  if (!result.valid) {
    if (result.error === "formato") {
      return true; // format validation is handled by earlier Yup schema
    }
    return props.createError({
      message: `E-mail deve ser de um dos seguintes domínios: ${result.allowedDomains}`,
    });
  }

  return true;
}

async function checkEmail(props, event_id) {
  try {
    const parentValues = props?.parent ?? props?.options?.context?.parent ?? {};

    // Check if email is already registered
    var response = await axios.get(
      `/api/event/checkEmail?event_id=${event_id}&email=${props.originalValue}`,
    );
    if (
      response.data.message > 0 &&
      String(response.data.message) !== String(parentValues.user_id ?? "")
    ) {
      return props.createError({ message: "E-mail já registrado" });
    } else {
      return true;
    }
  } catch (error) {
    console.error(error);
    return props.createError({ message: "Erro ao validar e-mail" });
  }
}

async function checkUser(props, input_id, label, event_id) {
  try {
    const parentValues = props?.parent ?? props?.options?.context?.parent ?? {};
    var response = await axios.get(
      `/api/event/checkVisitor?evento_id=${event_id}&id=${input_id}&value=${props.originalValue}&visitante_id=${parentValues.visitante_id ?? ""}`,
    );
    if (response.data.message > 0) {
      return props.createError({ message: label + " já registrado" });
    } else {
      return true;
    }
  } catch (error) {
    console.error(error);
    return props.createError({ message: label + " já registrado" });
  }
}

export const RFID_TAG_REGEX = /^(?:0x)?[0-9a-fA-F]{8,32}$/i;

export function normalizeRfidTag(tag) {
  if (typeof tag !== "string") return tag;
  const trimmed = tag.trim();
  if (!trimmed) return trimmed;
  const rawHex = (trimmed.startsWith("0x") || trimmed.startsWith("0X"))
    ? trimmed.slice(2)
    : trimmed;
  return `0x${rawHex.toUpperCase()}`;
}

export function validateDirectRfidInput(tag) {
  if (typeof tag !== "string" || !tag.trim()) {
    return {
      isValid: false,
      error: "Tag RFID é obrigatória",
      normalizedTag: null,
    };
  }

  const trimmed = tag.trim();

  if (!RFID_TAG_REGEX.test(trimmed)) {
    return {
      isValid: false,
      error: "Tag RFID inválida (deve conter entre 8 e 32 caracteres hexadecimais)",
      normalizedTag: null,
    };
  }

  return {
    isValid: true,
    error: null,
    normalizedTag: normalizeRfidTag(trimmed),
  };
}

function switchValidation(value, event_id, validEmailsConfig = null, { skipDuplicate = false } = {}) {
  if (value?.rfigtag === true || value?.validation === "rfid") {
    const rfidSchema = Yup.string()
      .nullable()
      .transform((val) => (typeof val === "string" ? val.trim() : val))
      .test(
        "rfid-format",
        "Tag RFID inválida (deve conter entre 8 e 32 caracteres hexadecimais)",
        (val) => {
          if (val === undefined || val === null || val === "") return true;
          return RFID_TAG_REGEX.test(val);
        }
      );

    if (value.primary) {
      return rfidSchema
        .required("Campo obrigatório")
        .test("rfid-duplicate", function (valueX) {
          if (skipDuplicate) return true;
          return new Promise((resolve, reject) => {
            resolve(checkUser(this, value.inputID, value.label, event_id));
          });
        });
    } else if (value.required) {
      return rfidSchema.required("Campo obrigatório");
    } else {
      return rfidSchema;
    }
  }

  switch (value.validation) {
    case "text":
      if (value.primary) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("text", function (valueX) {
            if (skipDuplicate) return true;
            return new Promise((resolve, reject) => {
              resolve(checkUser(this, value.inputID, value.label, event_id));
            });
          });
      } else if (value.required) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres");
      } else {
        return Yup.string().max("255", "Máximo de 255 caracteres");
      }
    case "textBig":
      if (value.primary) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("2500", "Máximo de 2500 caracteres")
          .test("text", function (valueX) {
            if (skipDuplicate) return true;
            return new Promise((resolve, reject) => {
              resolve(checkUser(this, value.inputID, value.label, event_id));
            });
          });
      } else if (value.required) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("2500", "Máximo de 2500 caracteres");
      } else {
        return Yup.string().max("2500", "Máximo de 2500 caracteres");
      }
    case "email":
      if (value.primary) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .email("E-mail inválido")
          .test("email-domain", function (valueX) {
            return checkEmailDomain(this, validEmailsConfig);
          })
          .test("email-duplicate", function (valueX) {
            // eslint-disable-next-line eqeqeq
            if (skipDuplicate) return true;
            return new Promise((resolve, reject) => {
              resolve(checkUser(this, value.inputID, value.label, event_id));
            });
          });
      } else if (value.required) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .email("E-mail inválido")
          .test("email-domain", function (valueX) {
            return checkEmailDomain(this, validEmailsConfig);
          });
      } else {
        return Yup.string()
          .max("255", "Máximo de 255 caracteres")
          .email("E-mail inválido")
          .test("email-domain", function (valueX) {
            return checkEmailDomain(this, validEmailsConfig);
          });
      }
    case "cpf":
      if (value.primary) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("CPF", "CPF inválido", function (valueX) {
            if (!validarCPF(valueX)) {
              return false;
            }
            if (skipDuplicate) return true;
            return new Promise((resolve, reject) => {
              resolve(checkUser(this, value.inputID, value.label, event_id));
            });
          });
      } else if (value.required) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("CPF", "CPF inválido", function (valueX) {
            if (!validarCPF(valueX)) {
              return false;
            } else {
              return true;
            }
          });
      } else {
        return Yup.string().max("255", "Máximo de 255 caracteres");
      }
    case "cnpj":
      if (value.primary) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("CNPJ", "CNPJ inválido", function (valueX) {
            if (!validarCNPJ(valueX)) {
              return false;
            }
            if (skipDuplicate) return true;
            return new Promise((resolve, reject) => {
              resolve(checkUser(this, value.inputID, value.label, event_id));
            });
          });
      } else if (value.required) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("CNPJ", "CNPJ inválido", function (valueX) {
            return validarCNPJ(valueX);
          });
      } else {
        return Yup.string()
          .max("255", "Máximo de 255 caracteres")
          .test("CNPJ", "CNPJ inválido", function (valueX) {
            return !valueX || validarCNPJ(valueX);
          });
      }
    case "telefone":
      if (value.primary) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("telefone", "Telefone inválido", function (valueX) {
            if (countStandAloneNumbers(valueX).length > 9) {
              if (skipDuplicate) return true;
              return new Promise((resolve, reject) => {
                resolve(checkUser(this, value.inputID, value.label, event_id));
              });
            } else {
              return false;
            }
          });
      } else if (value.required) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("telefone", "Telefone inválido", function (valueX) {
            if (countStandAloneNumbers(valueX).length > 9) {
              return true;
            } else {
              return false;
            }
          });
      } else {
        return Yup.string().max("255", "Máximo de 255 caracteres");
      }
    case "date":
      if (value.primary) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("date", "Data inválida", function (valueX) {
            if (validateDate(valueX)) {
              if (skipDuplicate) return true;
              return new Promise((resolve, reject) => {
                resolve(checkUser(this, value.inputID, value.label, event_id));
              });
            } else {
              return false;
            }
          });
      } else if (value.required) {
        return Yup.string()
          .required("Campo obrigatório")
          .max("255", "Máximo de 255 caracteres")
          .test("date", "Data inválida", function (valueX) {
            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          });
      } else {
        return Yup.string()
          .max("255", "Máximo de 255 caracteres")
          .test("date", "Data inválida", function (valueX) {
            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          });
      }
    case "number":
      if (value.primary) {
        return Yup.number()
          .typeError("Valor inválido, apenas números")
          .required("Campo obrigatório")
          .test("number", function (valueX) {
            if (skipDuplicate) return true;
            return new Promise((resolve, reject) => {
              resolve(checkUser(this, value.inputID, value.label, event_id));
            });
          })
          .test("number", "Máximo de 255 caracteres", function (valueX) {
            if (valueX) {
              if (valueX.toString().length <= 255) {
                return true;
              }
            }
            return false;
          });
      } else if (value.required) {
        return Yup.number()
          .typeError("Valor inválido, apenas números")
          .required("Campo obrigatório")
          .test("number", "Máximo de 255 caracteres", function (valueX) {
            if (valueX) {
              if (valueX.toString().length <= 255) {
                return true;
              }
            }
            return false;
          });
      } else {
        return Yup.number()
          .typeError("Valor inválido, apenas números")
          .test("number", "Máximo de 255 caracteres", function (valueX) {
            if (valueX) {
              if (valueX.toString().length <= 255) {
                return true;
              }
            }
            return false;
          });
      }
    case "checkbox":
      if (value.allowMultipleChoice) {
        if (value.required) {
          return Yup.array().test("required", "", function (valueX) {
            if (
              valueX === undefined ||
              valueX === "" ||
              valueX === "false" ||
              valueX === false ||
              !Array.isArray(valueX) ||
              valueX.length === 0
            ) {
              return this.createError({
                message: "Campo obrigatório",
              });
            } else {
              if (
                value.allowMultipleChoiceMin &&
                valueX.length < value.allowMultipleChoiceMin
              ) {
                return this.createError({
                  message: `Selecione no mínimo ${value.allowMultipleChoiceMin} alternativas`,
                });
              } else if (
                value.allowMultipleChoiceMax &&
                valueX.length > value.allowMultipleChoiceMax
              ) {
                return this.createError({
                  message: `Limite de ${value.allowMultipleChoiceMax} alternativas`,
                });
              } else {
                return validateMultiChoiceSelection(valueX, value, (error) =>
                  this.createError(error),
                );
              }
            }
          });
        } else {
          return Yup.array().test("required", "", function (valueX) {
            if (
              valueX === undefined ||
              valueX === null ||
              valueX === "" ||
              valueX === false ||
              valueX === "false"
            ) {
              return true;
            }

            if (!Array.isArray(valueX)) {
              return this.createError({
                message: "Formato inválido: este campo aceita uma lista de opções",
              });
            }

            if (
              value.allowMultipleChoiceMin &&
              (valueX ? valueX.length : 0) < value.allowMultipleChoiceMin
            ) {
              return this.createError({
                message: `Selecione no mínimo ${value.allowMultipleChoiceMin} alternativas`,
              });
            } else if (
              value.allowMultipleChoiceMax &&
              (valueX ? valueX.length : 0) > value.allowMultipleChoiceMax
            ) {
              return this.createError({
                message: `Limite de ${value.allowMultipleChoiceMax} alternativas`,
              });
            } else {
              return validateMultiChoiceSelection(valueX, value, (error) =>
                this.createError(error),
              );
            }
          });
        }
      } else {
        if (value.required) {
          return Yup.string()
            .nullable()
            .test("required", "", function (valueX) {
              if (
                valueX === undefined ||
                valueX === "" ||
                valueX === "false" ||
                valueX === false ||
                valueX === null
              ) {
                return this.createError({
                  message: "Campo obrigatório",
                });
              } else {
                return validateSingleChoiceSelection(valueX, value, (error) =>
                  this.createError(error),
                );
              }
            });
        } else {
          return Yup.string()
            .nullable()
            .test("valid-option", "", function (valueX) {
              return validateSingleChoiceSelection(valueX, value, (error) =>
                this.createError(error),
              );
            });
        }
      }

    default:
      return "";
  }
}

export function countStandAloneNumbers(str) {
  if (str === undefined) {
    str = "";
  }
  return str.replace(/\D/g, "");
}

export function validarCPF(cpf) {
  cpf = countStandAloneNumbers(cpf);
  // Verifica se o CPF possui 11 dígitos
  if (cpf.length !== 11) {
    return false;
  }

  // Verifica se todos os dígitos são iguais (CPF inválido, mas passaria na validação do primeiro dígito)
  const digitosIguais = [...new Set(cpf.split(""))].length === 1;
  if (digitosIguais) {
    return false;
  }

  // Calcula o primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = (soma * 10) % 11;
  let digitoVerificador1 = resto === 10 || resto === 11 ? 0 : resto;

  // Verifica o primeiro dígito verificador
  if (parseInt(cpf.charAt(9)) !== digitoVerificador1) {
    return false;
  }

  // Calcula o segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = (soma * 10) % 11;
  let digitoVerificador2 = resto === 10 || resto === 11 ? 0 : resto;

  // Verifica o segundo dígito verificador
  if (parseInt(cpf.charAt(10)) !== digitoVerificador2) {
    return false;
  }

  return true; // CPF válido
}

export function validarCNPJ(cnpj) {
  cnpj = countStandAloneNumbers(cnpj);

  if (cnpj.length !== 14 || [...new Set(cnpj.split(""))].length === 1) {
    return false;
  }

  const calculateDigit = (value, factor) => {
    let sum = 0;
    for (let index = 0; index < value.length; index += 1) {
      sum += Number(value[index]) * factor;
      factor -= 1;
      if (factor < 2) factor = 9;
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstDigit = calculateDigit(cnpj.slice(0, 12), 5);
  const secondDigit = calculateDigit(cnpj.slice(0, 12) + firstDigit, 6);

  return firstDigit === Number(cnpj[12]) && secondDigit === Number(cnpj[13]);
}

export function checkMediaType(url) {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
  const extension = url.split(".").pop().toLowerCase();
  return imageExtensions.includes(extension) ? "image" : "video";
}

export function getMediaType(url) {
  if (!url || typeof url !== "string") return null;
  const clean = url.split("?")[0].split("#")[0];
  return checkMediaType(clean);
}
